import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Wrench, Check, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { devicesApi } from '@/api/endpoints/devices';
import { useFleetStore } from '@/stores/fleet';
import { useSessionStore } from '@/stores/session';
import type { Device } from '@/api/types';

/** Testes de instalação (técnico instalador — seção 19). */
const TESTES: { key: string; label: string }[] = [
  { key: 'ignicao', label: 'Ignição detectada' },
  { key: 'bloqueio', label: 'Bloqueio/desbloqueio do motor' },
  { key: 'sirene', label: 'Sirene' },
  { key: 'sensor_agua', label: 'Sensor de água / bomba de porão' },
  { key: 'bateria', label: 'Tensão da bateria' },
  { key: 'comunicacao', label: 'Comunicação (servidor recebe)' },
  { key: 'gps', label: 'Posição GPS válida' },
];

function today(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function InstallPage() {
  const qc = useQueryClient();
  const devices = useFleetStore((s) => s.devices);
  const techName = useSessionStore((s) => s.user?.name ?? '');

  const [imei, setImei] = useState('');
  const [device, setDevice] = useState<Device | null>(null);
  const [tests, setTests] = useState<Record<string, boolean>>({});
  const [tech, setTech] = useState(techName);
  const [local, setLocal] = useState('');
  const [obs, setObs] = useState('');
  const [done, setDone] = useState(false);

  const list = useMemo(() => Array.from(devices.values()), [devices]);

  const buscar = () => {
    const q = imei.trim().toLowerCase();
    const d = list.find((x) => x.uniqueId?.toLowerCase() === q || x.name?.toLowerCase() === q);
    setDevice(d ?? null);
    setDone(false);
    if (d) {
      const a = d.attributes ?? {};
      const inst = (a.instalacao as Record<string, unknown> | undefined) ?? {};
      setTests((inst.tests as Record<string, boolean>) ?? {});
      setTech((inst.tech as string) || techName);
      setLocal((inst.local as string) || (typeof d.localInstall === 'string' ? d.localInstall : ''));
      setObs((inst.obs as string) || '');
    }
  };

  const aprovar = useMutation({
    mutationFn: () => {
      if (!device) throw new Error('Sem equipamento');
      const okCount = TESTES.filter((t) => tests[t.key]).length;
      const data = today();
      const payload = {
        ...device,
        tech: tech.trim() || undefined,
        localInstall: local.trim() || undefined,
        dateInstall: data,
        attributes: {
          ...(device.attributes ?? {}),
          statusEquip: 'instalado',
          instalacao: {
            aprovado: okCount === TESTES.length,
            tests,
            tech: tech.trim() || undefined,
            local: local.trim() || undefined,
            data,
            obs: obs.trim() || undefined,
            testes_ok: okCount,
            testes_total: TESTES.length,
          },
        },
      } as Device;
      return devicesApi.update(device.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices'] });
      setDone(true);
    },
  });

  const okCount = TESTES.filter((t) => tests[t.key]).length;
  const allOk = okCount === TESTES.length;

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <Wrench className="h-5 w-5 text-primary" /> Ativação Técnica
        </h1>
        <p className="text-sm text-muted-foreground">
          Localize o equipamento pelo IMEI, rode os testes de instalação e aprove a ativação.
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <label className="text-xs font-semibold text-muted-foreground">IMEI / ID único do rastreador</label>
          <div className="mt-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={imei}
                onChange={(e) => setImei(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
                placeholder="Ex.: 869731052532447"
                className="pl-8"
              />
            </div>
            <Button onClick={buscar}>Buscar</Button>
          </div>
          {imei.trim() && device === null && (
            <p className="mt-2 text-xs text-status-blocked">
              Equipamento não encontrado na frota. Cadastre a embarcação primeiro (Configurações → Adicionar embarcação).
            </p>
          )}
        </CardContent>
      </Card>

      {device && (
        <>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="truncate font-bold">{device.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[device.uniqueId, device.model, device.hardware, device.vendor].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <span className="rounded-full bg-status-ready/15 px-2 py-0.5 text-[10px] font-bold text-status-ready">
                  {okCount}/{TESTES.length} testes
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 p-4">
              <h2 className="text-sm font-bold">Testes de instalação</h2>
              {TESTES.map((t) => (
                <label key={t.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!tests[t.key]}
                    onChange={(e) => setTests((s) => ({ ...s, [t.key]: e.target.checked }))}
                    className="h-4 w-4 accent-primary"
                  />
                  {t.label}
                </label>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-4">
              <h2 className="text-sm font-bold">Dados da instalação</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <L label="Técnico"><Input value={tech} onChange={(e) => setTech(e.target.value)} /></L>
                <L label="Local de instalação"><Input value={local} onChange={(e) => setLocal(e.target.value)} placeholder="Ex.: console / popa" /></L>
              </div>
              <L label="Observações"><Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Notas da instalação" /></L>
              <p className="text-[10px] text-muted-foreground">Data registrada automaticamente: {today()}. Aprovar marca o equipamento como <strong>instalado</strong>.</p>
            </CardContent>
          </Card>

          {aprovar.error && <p className="text-xs text-status-blocked">{(aprovar.error as Error).message}</p>}
          {done ? (
            <div className="flex items-center gap-2 rounded-md bg-status-running/10 px-3 py-2 text-sm font-semibold text-status-running">
              <Check className="h-4 w-4" /> Instalação registrada{allOk ? ' e aprovada' : ' (com pendências de teste)'}.
            </div>
          ) : (
            <Button onClick={() => aprovar.mutate()} disabled={aprovar.isPending} className="w-full gap-1.5">
              <ShieldCheck className="h-4 w-4" />
              {aprovar.isPending ? 'Salvando…' : allOk ? 'Aprovar instalação' : 'Registrar instalação (com pendências)'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
